import { Alert, AlertColor, Snackbar } from '@mui/material';
import { createContext, useContext } from 'react';
import { useImmer } from 'use-immer';
import { getSnackbarUi, initialState, SnackbarUi } from './SnackBarUi';

export interface SnackContextDef {
  showSnackSuccess: (message: string, autoHideDuration?: number) => void;
  showSnackError: (message: string, autoHideDuration?: number) => void;
  showSnackInfo: (message: string, autoHideDuration?: number) => void;
  showSnackWarning: (message: string, autoHideDuration?: number) => void;
}

export const SnackContext = createContext<SnackContextDef>({
  showSnackSuccess: () => undefined,
  showSnackError: () => undefined,
  showSnackInfo: () => undefined,
  showSnackWarning: () => undefined,
});

export const useSnackbarContext = () => {
  const context = useContext(SnackContext);
  if (!context) {
    throw new Error('component is not inside SnackContext');
  }
  return context;
};

export const SnackbarProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [snackUi, setSnackUi] = useImmer<SnackbarUi>(initialState);

  const showSnack = (
    type: AlertColor,
    message: string,
    autoHideDuration?: number
  ) =>
    setSnackUi(
      getSnackbarUi({
        type,
        message,
        open: true,
        autoHideDuration,
      })
    );

  const showSnackSuccess = (message: string, autoHideDuration?: number) =>
    showSnack('success', message, autoHideDuration);

  const showSnackError = (message: string, autoHideDuration?: number) =>
    showSnack('error', message, autoHideDuration);

  const showSnackInfo = (message: string, autoHideDuration?: number) =>
    showSnack('info', message, autoHideDuration);

  const showSnackWarning = (message: string, autoHideDuration?: number) =>
    showSnack('warning', message, autoHideDuration);

  const handleSnackClose = () =>
    setSnackUi((prev) => {
      prev.open = false;
    });

  return (
    <SnackContext.Provider
      value={{
        showSnackSuccess,
        showSnackError,
        showSnackWarning,
        showSnackInfo,
      }}
    >
      {children}
      <Snackbar
        open={snackUi.open}
        autoHideDuration={snackUi.autoHideDuration}
        onClose={handleSnackClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        <Alert
          onClose={handleSnackClose}
          icon={snackUi.icon}
          sx={{
            backgroundColor: snackUi.backgroundColor,
            border: 'solid 1px',
            borderRadius: '8px',
            color: snackUi.color,
            borderColor: snackUi.borderColor,
          }}
          severity={snackUi.type}
        >
          {snackUi.message}
        </Alert>
      </Snackbar>
    </SnackContext.Provider>
  );
};
